import { useMemo } from 'react';

import { useRevisionScanIssueTypes } from '../../../hooks/api/revisions/useRevisionScanIssueTypes';

import { useRevisionScanIdQueryParam } from './useRevisionScanIdQueryParam';

type UseRevisionIssueTypeSummaryParams = {
  // Optional because we skip fetching if not provided (see shouldFetch condition below)
  // This allows consumers to pass revision.data?.locator without type errors
  revisionLocator?: string;
  latestRevisionScanId?: number;
};

/**
 * Convenience hook for fetching and managing revision issue types summary.
 *
 * This hook:
 * - Reads the selected revision scan ID from URL query params
 * - Falls back to `latestRevisionScanId` if no query param is present
 * - Fetches issue types summary using `useRevisionScanIssueTypes`
 * - Computes the total issue count across all types
 *
 * The URL query param `?revisionScanId=X` is the source of truth for which scan is selected.
 * When this param changes, the hook automatically refetches the issue types summary.
 *
 * @param params - Parameters for the hook
 * @param params.revisionLocator - The full revision locator (e.g., "git+github.com/owner/repo$abc123")
 * @param params.latestRevisionScanId - Fallback scan ID to use if no query param is present
 *
 * @returns Object containing:
 * - issueTypesSummary: Map of issue type to count
 * - totalIssueCount: Sum of all issue counts
 * - isLoading: Loading state
 * - error: Error if fetch failed
 * - revisionScanId: The scan ID being used (from query param or fallback)
 *
 * @example
 * ```typescript
 * const {
 *   issueTypesSummary,
 *   totalIssueCount,
 *   revisionScanId,
 * } = useRevisionIssueTypeSummary({
 *   revisionLocator: revision.data?.locator,
 *   latestRevisionScanId: revision.data?.latestRevisionScanId,
 * });
 * ```
 */
export function useRevisionIssueTypeSummary({
  revisionLocator,
  latestRevisionScanId,
}: UseRevisionIssueTypeSummaryParams) {
  // Read revisionScanId from URL query params, with fallback
  const { revisionScanId: revisionScanIdFromQuery } = useRevisionScanIdQueryParam();
  const revisionScanId = revisionScanIdFromQuery || latestRevisionScanId;

  // Fetch issue types summary
  // Note: We only fetch if revisionLocator is truthy. If revisionScanId is not present, the backend will use the latest
  const { data, isLoading, error } = useRevisionScanIssueTypes({
    revisionLocator: revisionLocator || '',
    revisionScanId,
    shouldFetch: !!revisionLocator,
  });

  // Compute total count (memoized to avoid unnecessary recalculation)
  const totalIssueCount = useMemo(
    () => (data ? Object.values(data).reduce((sum, count) => sum + count, 0) : undefined),
    [data],
  );

  return {
    issueTypesSummary: data,
    totalIssueCount,
    isLoading,
    error,
    revisionScanId,
  };
}
